import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Heart, TrendingUp, Activity } from "lucide-react";

const HeartWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    // Find heart tab
    const tabs = t("tabs", { returnObjects: true }) as any[];
    const heartTab = tabs.find((tab) => tab.id === "heart");
    const widget = heartTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white via-white to-red-50 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-red-100 hover:shadow-red-200/50 transition-all duration-300">
                <div className="flex justify-between items-center mb-3">
                    <h5 className="text-red-800 font-medium flex items-center">
                        <motion.div
                            className={`inline-block ${isRTL ? "ml-2" : "mr-2"} text-red-500`}
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 1 }}
                        >
                            <Heart className="w-5 h-5 fill-current" />
                        </motion.div>
                        {widget.title}
                    </h5>
                    <motion.div
                        className="flex items-center bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-full shadow-sm"
                        whileHover={{ scale: 1.05, boxShadow: "0 3px 10px rgba(239, 68, 68, 0.3)" }}
                    >
                        <motion.div className={`inline-block ${isRTL ? "ml-1" : "mr-1"}`} animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.8, repeat: Infinity }}>
                            <Heart className="w-4 h-4" />
                        </motion.div>
                        <span className="text-sm font-medium">{widget.bpm}</span>
                        <span className="text-xs mr-2">BPM</span>
                    </motion.div>
                </div>

                {/* Heart rate display */}
                <div className="relative h-36 mb-3 overflow-hidden rounded-lg bg-gradient-to-br from-pink-50 to-rose-50 flex items-center justify-center group">
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-pink-400/10 to-rose-500/10 opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                        animate={{ scale: [1, 1.03, 1] }}
                        transition={{ duration: 5, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                    />

                    {/* Circular display */}
                    <div className="relative flex items-center justify-center w-full h-full">
                        <motion.div
                            className="absolute w-28 h-28 rounded-full border-4 border-pink-100"
                            animate={{
                                boxShadow: ["0 0 0px rgba(236, 72, 153, 0.05)", "0 0 20px rgba(236, 72, 153, 0.15)", "0 0 0px rgba(236, 72, 153, 0.05)"],
                            }}
                            transition={{ duration: 3.5, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                        />

                        <motion.div
                            className="absolute w-20 h-20 rounded-full bg-gradient-to-br from-pink-400/10 to-rose-400/10 flex items-center justify-center"
                            animate={{ scale: [1, 1.04, 1] }}
                            transition={{ duration: 2.5, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                        >
                            <motion.div
                                className="absolute w-full h-full rounded-full"
                                animate={{
                                    boxShadow: ["0 0 0px rgba(219, 39, 119, 0.1)", "0 0 15px rgba(219, 39, 119, 0.2)", "0 0 0px rgba(219, 39, 119, 0.1)"],
                                }}
                                transition={{ duration: 2.5, repeat: Infinity, repeatType: "mirror", delay: 0.3, ease: "easeInOut" }}
                            />
                        </motion.div>

                        <motion.div
                            className="absolute w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-md z-10"
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 1.8, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                        >
                            <motion.span
                                className="text-xl font-bold text-white"
                                animate={{ opacity: [1, 0.85, 1] }}
                                transition={{ duration: 1.8, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
                            >
                                {widget.bpm}
                            </motion.span>
                        </motion.div>
                    </div>
                </div>

                {/* Hourly chart */}
                <div className="mb-3">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">{widget.hourlyChart}</span>
                        <span className="text-xs text-gray-500">{widget.last24h}</span>
                    </div>
                    <div className="h-12 bg-red-50/50 rounded-lg overflow-hidden relative">
                        <div className="flex items-end h-full w-full px-1 pt-1 pb-1 absolute bottom-0 left-0 right-0">
                            {[65, 68, 72, 75, 80, 78, 72, 70, 68, 72, 74, 72].map((value, index) => (
                                <motion.div
                                    key={index}
                                    className="flex-1 mx-0.5 rounded-t-sm bg-gradient-to-t from-red-500 to-red-400"
                                    style={{ height: `${(value - 60) * 4}%` }}
                                    initial={{ scaleY: 0 }}
                                    animate={{ scaleY: 1 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    whileHover={{ scaleY: 1.1, backgroundColor: "#ef4444", transition: { duration: 0.2 } }}
                                />
                            ))}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-200/50"></div>
                    </div>
                </div>

                {/* Range and comparison */}
                <div className="flex justify-between items-center mb-3">
                    <div>
                        <span className="text-sm text-gray-600 block mb-1">{widget.rangeToday.label}</span>
                        <div className="flex items-center">
                            <span className="text-sm font-medium text-red-700">{widget.rangeToday.value}</span>
                            <span className="text-xs text-gray-500 mr-2 mb-0.5">BPM</span>
                        </div>
                    </div>
                    <div>
                        <span className="text-sm text-gray-600 block mb-1">{widget.weeklyAvg.label}</span>
                        <div className="flex items-center">
                            <motion.div className="w-4 h-4 ml-1" animate={{ rotate: [0, 0, -10, 10, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}>
                                <TrendingUp className="w-4 h-4 text-green-600" />
                            </motion.div>
                            <span className="text-sm font-medium text-green-600">{widget.weeklyAvg.improvement}</span>
                            <span className="text-xs text-gray-500 mr-1 mb-1">{widget.weeklyAvg.improvementLabel}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mt-3">
                {/* Status widget */}
                <motion.div
                    className="bg-gradient-to-br from-white to-red-50/50 backdrop-blur-sm rounded-xl p-3 border border-red-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(239, 68, 68, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-red-700 font-medium">{widget.status.label}</span>
                        <motion.div className="text-green-500 w-5 h-5" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                            </svg>
                        </motion.div>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{widget.status.rest}</span>
                        <motion.div className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-md flex items-center" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 ml-1"></span>
                            {widget.status.value}
                        </motion.div>
                    </div>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ width: "75%" }}
                            transition={{ duration: 1, delay: 0.5 }}
                        />
                    </div>
                </motion.div>

                {/* Variability widget */}
                <motion.div
                    className="bg-gradient-to-br from-white to-red-50/50 backdrop-blur-sm rounded-xl p-3 border border-red-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(239, 68, 68, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-red-700 font-medium">{widget.variability.label}</span>
                        <motion.div className="text-blue-500 w-5 h-5" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                            <Activity className="w-5 h-5" />
                        </motion.div>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{widget.variability.sublabel}</span>
                        <motion.div className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-md flex items-center" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 ml-1"></span>
                            {widget.variability.value}
                        </motion.div>
                    </div>
                    <div className="mt-2 relative h-1.5">
                        <div className="absolute inset-0 flex items-center justify-between">
                            {[1, 2, 3, 4, 5].map((_, index) => (
                                <motion.div
                                    key={index}
                                    className="w-1 h-1 rounded-full bg-blue-400"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                />
                            ))}
                        </div>
                        <div className="absolute inset-0 flex items-center">
                            <motion.div className="h-0.5 bg-blue-200 w-full" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8 }} />
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default HeartWidget;
